import React, { useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { TRUSTS_COLUMNS } from "./columns";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import "./checklisttable.scss";
import PropTypes from "prop-types";
import privateAxios from "../../../../utils/axiosPrivate";
import { sanitizeInput, showToast } from "../../../../utils/utils";
import { showToastType } from "../../../../utils/constants/appConstants";
import { getSalesTrustCrprteUpdateComment } from "../../../../utils/constants/apiEndPoints";
import { ddMMMyyyy } from "../../../../utils/constants/appConstants";
import { ownerTab, TrustCorporate } from "../../../../locales/en";

const TrustsTable = ({ trustCorporates: initialTrustCorporates, userId }) => {
  const [trustCorporates, setTrustCorporates] = useState(
    initialTrustCorporates
  );
  const columns = useMemo(() => TRUSTS_COLUMNS, []);

  const data = useMemo(() => {
    const nestedTrusts = trustCorporates?.trustCorporates?.trustCorporate;
    if (!Array.isArray(nestedTrusts) || nestedTrusts.length === 0) return [];

    const getDirectorName = (person) => {
      if (!person) return "";

      const firstName = person.firstName?.trim();
      const lastName = person.lastName?.trim();

      // If both firstName and lastName are missing, show entityName
      if (!firstName && !lastName) {
        return person.entityName || "";
      }

      return `${firstName || ""} ${lastName || ""}`.trim();
    };

    return nestedTrusts
      .filter((item) => (item.entityName || "").trim() !== "")
      .map((item) => {
        const people = (item.corporateTrustAssociates || []).sort(
          (a, b) => a.orderNumber - b.orderNumber
        );

        const notesMap = trustCorporates?.notes || {};
        const noteById = notesMap[item.id] || "";

        return {
          id: item.id,
          trusts_corporates: item.entityName || "",
          director_1: getDirectorName(people[0]),
          director_2: getDirectorName(people[1]),
          director_3: getDirectorName(people[2]),
          fundtap_notes: noteById || item.entityComment || ""
        };
      });
  }, [trustCorporates]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data,
        initialState: { pageIndex: 0 }
      },
      useFilters,
      useGlobalFilter,
      useExpanded,
      usePagination,
      useRowSelect
    );

  // Expand/Collapse logic
  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const isRowExpanded = (rowId) => expandedRows.includes(rowId);

  const updateCommandData = useCallback(
    async (trustCorporateId, notes) => {
      try {
        const response = await privateAxios.put(
          getSalesTrustCrprteUpdateComment,
          {
            userAccountId: parseInt(userId, 10),
            trustCorporateId,
            notes: sanitizeInput(notes)
          }
        );
        showToast(
          `${TrustCorporate} ${response?.data?.message || ownerTab?.directorsOwner?.errMsg?.item1}`,
          showToastType.success
        );
        if (response?.data) setTrustCorporates(response?.data?.data);
      } catch (error) {
        showToast(error?.message, showToastType.error);
      }
    },
    [userId]
  );

  const [editingNotes, setEditingNotes] = useState({});

  return (
    <table
      className="table-fixed mt-2 mb-3 businesstable borderless_table bgless_table"
      {...getTableProps()}
    >
      <thead>
        {headerGroups.map((headerGroup) => {
          const { key, ...rest } = headerGroup.getHeaderGroupProps();
          return (
            <tr className="parentrow" {...rest} key={key}>
              <th />
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()} key={column.id}>
                  <span>{column.render("Header")}</span>
                </th>
              ))}
            </tr>
          );
        })}
      </thead>

      <tbody {...getTableBodyProps()}>
        {page.map((row) => {
          prepareRow(row);
          const isExpanded = isRowExpanded(row.id);

          return (
            <React.Fragment key={row.id}>
              <tr className="parentrow" {...row.getRowProps()}>
                <td className="expand-td">
                  <span
                    className="expandbtn cursor-pointer"
                    onClick={() => toggleRow(row.id)}
                  >
                    {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                  </span>
                </td>

                {row.cells.map((cell) => {
                  if (cell.column.id === "fundtap_notes") {
                    return (
                      <td
                        key={cell.getCellProps().key}
                        {...cell.getCellProps({
                          className: cell.column.className
                        })}
                      >
                        {cell.render("Cell", {
                          updateSalesData: (rowId, value) => {
                            const original =
                              data.find((r) => r.id === rowId)?.fundtap_notes ||
                              "";
                            if ((value || "") !== (original || "")) {
                              updateCommandData(rowId, value);
                            }
                          },
                          commentData: editingNotes,
                          setCommentData: setEditingNotes,
                          rowId: row.original.id,
                          noteValue: row.original.fundtap_notes
                        })}
                      </td>
                    );
                  }

                  return (
                    <td
                      key={cell.getCellProps().key}
                      {...cell.getCellProps({
                        className: cell.column.className
                      })}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>

              {isExpanded && (
                <tr className="childrow colexpand">
                  <td colSpan={columns.length + 2}>
                    <div className="tdbody">
                      {columns.map((column) => (
                        <span key={column.accessor} data-label={column.Header}>
                          {column.accessor === "date"
                            ? format(
                                new Date(row.original[column.accessor]),
                                ddMMMyyyy
                              )
                            : String(row.original[column.accessor])}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

TrustsTable.propTypes = {
  trustCorporates: PropTypes.object,
  userId: PropTypes.string
};

export default TrustsTable;
